import { Result } from "../../models/result.js";
import { getFirestore, collection, getDocs, query, orderBy } from "firebase/firestore";

const db = getFirestore();

/**
 * Fetches all subjects for a given school and class from Firestore.
 * Queries collection: schools/{schoolId}/classes/{classId}/subjects
 * 
 * @param {string} schoolId - Unique School identifier (mandatory)
 * @param {string|number} classId - Class identifier (mandatory)
 * @returns {Promise<Result>} List of subject documents for the class
 */
export const getAllSubjects = async (schoolId, classId) => {
    try {
        if (!schoolId) {
            return new Result({ message: "schoolId is required to fetch subjects" });
        }
        if (classId === undefined || classId === null || String(classId).trim() === '') {
            return new Result({ message: "classId is required to fetch subjects" });
        }

        const classSubjectsRef = collection(db, "schools", schoolId, "classes", String(classId), "subjects");
        let querySnapshot;
        try {
            const q = query(classSubjectsRef, orderBy("name", "asc"));
            querySnapshot = await getDocs(q);
        } catch (orderErr) {
            // Fallback without orderBy in case index or field varies
            querySnapshot = await getDocs(classSubjectsRef);
        }

        const subjects = [];
        querySnapshot.forEach((doc) => {
            const data = doc.data();
            subjects.push({
                id: doc.id,
                name: data.name || data.subject || doc.id,
                ...data
            });
        });

        // Ensure unique subjects by name if duplicates exist
        const uniqueSubjects = [];
        const seenNames = new Set();
        for (const subj of subjects) {
            const nameKey = (subj.name || '').trim().toLowerCase();
            if (nameKey && !seenNames.has(nameKey)) {
                seenNames.add(nameKey);
                uniqueSubjects.push(subj);
            }
        }

        // Sort alphabetically
        uniqueSubjects.sort((a, b) => (a.name || '').localeCompare(b.name || ''));

        return new Result({
            value: uniqueSubjects
        });
    } catch (ex) {
        console.error(`[subjectRepo] Error fetching subjects for class ${classId}:`, ex);
        return new Result({
            message: `Error fetching subjects: ${ex.toString()}`
        });
    }
};

/**
 * Alias for getAllSubjects for class compatibility
 */
export const getSubjectsOfClass = getAllSubjects;
